import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import { useSelector, useDispatch } from "react-redux";
import { TextField, Autocomplete, MenuItem } from "@mui/material";
import * as Yup from "yup";
import CheckIcon from "@mui/icons-material/Check";
// css
import "./Addprojects.css";
import pdfimg from "../../../Assets/images/pdf_img.png";
import uploadicon from "../../../Assets/images/upload_icon.png";
import urllink from "../../../Assets/images/url.png";
// custom import
import showToast from "../../../Utils/Toast/ToastNotification";
import { HandleApiError } from "../../../Utils/CommonErrors/HandleApiError";
import projectManagementService from "../../../Services/projectManagementService/projectManagementService";
import userManagementService from "../../../Services/UserManagement/UserManagementService";
import {
  hideLoader,
  showLoader,
} from "../../../Redux/Slice/Loader/loaderSlice";

const AddProject = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const created_by = useSelector(
    (state) => state.auth.user.data?.body?.data?.userData?.id
  );
  const token = useSelector((state) => state.auth?.user?.data?.body?.data?.token)

  const [states, setStates] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [users, setUsers] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");

  const initialValues = {
    projectStreetAddress: "",
    projectSuburb: "",
    projectState: "",
    projectType: "",
    projectCoordinators: "",
    startDate: "",
    endDate: "",
    projectStatus: "New",
    projectPlan: "",
    otherFiles: [],
    created_by: created_by,
    user_id: created_by,
    last_updated_by: created_by,
    client_name: "",
    client_email: "",
    document_urls: []
  };

  const validationSchema = Yup.object().shape({
    projectStreetAddress: Yup.string().required(
      "Project street address is required"
    ),
    projectSuburb: Yup.string().required("Project suburb is required"),
    projectState: Yup.string().required("Project state is required"),
    projectType: Yup.string().required("Project type is required"),
    projectCoordinators: Yup.string().required(
      "Project coordinators are required"
    ),
    postCode: Yup.string()
      .typeError("Post Code must be a number")
      .required("Post Code is required"),
    startDate: Yup.date().required("Start date is required"),
    endDate: Yup.date()
      .nullable()
      .min(Yup.ref("startDate"), "End date must be after start date"),
    projectStatus: Yup.string().required("status is required"),
    projectPlan: Yup.mixed()
      .nullable()
      .test("fileType", "Only PDF files are allowed", (value) => {
        if (!value) return true;
        return value.type === "application/pdf";
      })
      .test("fileSize", "File size must be less than 10MB", (value) => {
        if (!value) return true;
        return value.size <= 10 * 1024 * 1024;
      }),
    otherFiles: Yup.mixed()
      .nullable()
      .test("fileNumber", "You can upload a maximum of 5 files", (value) => {
        if (!value) return true;
        return value.length <= 5;
      })
      .test("fileSize", "Each file must be less than 10MB", (value) => {
        if (!value) return true;
        return value.every((file) => file.size <= 10 * 1024 * 1024);
      }),
    client_email: Yup.string().email("Invalid email address").nullable(),
    document_urls: Yup.array()
      .of(Yup.string().url("Each document URL must be valid"))
      .nullable(),
  });

  const onSubmit = async (values, { resetForm }) => {
    dispatch(showLoader());
    const isArchive = values.projectStatus === "Archive" ? true : false;
    const formData = new FormData();
    const fieldMapping = {
      projectStreetAddress: "streetAddress",
      projectSuburb: "suburb",
      projectState: "state",
      projectType: "projectType",
      projectCoordinators: "user_id_array",
      postCode: "postCode", // Example (agar backend `zipCode` expect kare)
      startDate: "startDate",
      endDate: "endDate",
      projectStatus: "status",
      projectPlan: "planFileUrl",
      otherFiles: "otherFiles",
      client_name: "client_name",
      client_email: "client_email",
      document_urls: "document_urls"
    };

    Object.entries(values).forEach(([key, value]) => {
      const backendKey = fieldMapping[key] || key; // Default to same key if no mapping
      if (value instanceof File) {
        // Agar value ek file hai, toh FormData me directly bhejo
        formData.append(backendKey, value);
      } else if (
        Array.isArray(value) &&
        value.every((val) => val instanceof File)
      ) {
        // If it's an array of files, append each file individually
        value.forEach((file, index) => {
          formData.append(backendKey, file); // You can also append with a unique index if needed
        });
      } else if (backendKey !== "is_archive" && value !== null && value !== undefined) {
        // Append other values to FormData
        formData.append(backendKey, value.toString());
      } else if (value !== null && value !== undefined) {
        // Agar normal value hai toh usko string me convert karo
        formData.append(backendKey, value.toString());
      }
    });
    if (!formData.has('is_archive')) {
      // Check if 'is_archive' is being added
      formData.append("is_archive", isArchive);
    }

    try {
      const response = await projectManagementService.createProject(formData, token);
      if (response.status === 200) {
        showToast("success", "Project added successfully");
        resetForm();
        navigate("/project-listing");
      }
    } catch (error) {
      await HandleApiError(error);
    } finally {
      dispatch(hideLoader());
    }
  };

  const getStates = async () => {
    try {
      const response = await projectManagementService.getStates(token);
      if (response?.data?.statusCode === 200) {
        setStates(response.data.body.data.states);
      } else {
        showToast("error", response.data.body.message);
      }
    } catch (error) {
      await HandleApiError(error);
    }
  };

  const getProjectTypes = async () => {
    try {
      const response = await projectManagementService.getProjectTypes(token);
      if (response?.data?.statusCode === 200) {
        setProjectTypes(response.data.body.data.states);
      } else {
        showToast("error", response.data.body.message);
      }
    } catch (error) {
      await HandleApiError(error);
    }
  };

  const getUser = async () => {
    try {
      const params = {
        search: searchQuery || "",
        limit: 100,
      };
      const response = await userManagementService.getUserList(params, token);
      if (response?.data?.statusCode === 200) {
        setUsers(response.data.body.data.users);
      } else {
        showToast("error", response.data.body.message);
      }
    } catch (error) {
      await HandleApiError(error);
    }
  };

  useEffect(() => {
    getStates();
    getProjectTypes();
    getUser();
  }, []);

  return (
    <>
      <div className="main-inner-block">
        <div className="heading-content">Add Project</div>
        <div className="content">
          <div className="white-box">
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {({
                values,
                setFieldValue,
                handleChange,
                handleBlur,
                handleSubmit,
                errors,
                touched,
              }) => (
                <Form>
                  <div class="create-user-block">
                    <div className="add-project-area ">

                      {/* project details block */}
                      <div className="add-project-inner">
                        <h6 className="heading-content m-0 heading-text">Project Details</h6>
                        <div className="project-details-section row">
                          <div className="add-project-box col-md-4 mb-3">
                            <label htmlFor="projectType" className="form-label">
                              Project Type<span>*</span>
                            </label>
                            <select
                              className="form-select"
                              name="projectType"
                              id="projectType"
                              value={values.projectType}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            >
                              <option value="" disabled>Project Type</option>
                              {Array.isArray(projectTypes) &&
                                projectTypes.length > 0 ? (
                                projectTypes.map((projectTyepe_id) => (
                                  <option
                                    key={projectTyepe_id.id}
                                    value={projectTyepe_id.id}
                                  >
                                    {projectTyepe_id.name}
                                  </option>
                                ))
                              ) : (
                                <option disabled>No project types available</option>
                              )}
                            </select>
                            {errors.projectType && touched.projectType && (
                              <div className="error">{errors.projectType}</div>
                            )}
                          </div>

                          <div className="add-project-box col-md-4 mb-3">
                            <label
                              htmlFor="projectCoordinators"
                              className="form-label"
                            >
                              Project Coordinators<span>*</span>
                            </label>
                            <Autocomplete
                              className="autocomplete-box position-relative proj-coordinators-field"
                              sx={{
                                m: 1,
                                // width: 375,
                                "& .MuiOutlinedInput-root": {
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderBottom: "1px solid #e6e6e6 !important", // Remove the default border
                                  },
                                  "&:before, &:after": {
                                    borderBottom: "1px solid #e6e6e6 !important", // Ensure it applies
                                  },
                                  "&.Mui-focused:after": {
                                    borderBottom: "1px solid #e6e6e6 !important", // Override focus blue border
                                  },
                                },
                              }}
                              multiple
                              options={users}
                              getOptionLabel={(option) =>
                                `${option.first_name} ${option.last_name}`
                              }
                              disableCloseOnSelect
                              onChange={(event, newValue) => {
                                const selectedCoordinators = newValue
                                  .map((user) => user.user_id)
                                  .join(",");
                                setFieldValue(
                                  "projectCoordinators",
                                  selectedCoordinators
                                );
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  variant="outlined"
                                  // label="Co-ordinators"
                                  // placeholder="co-ordinators"
                                  className="custom-input-box"
                                />
                              )}
                              renderOption={(props, option, { selected }) => (
                                <MenuItem
                                  {...props}
                                  key={option.user_id}
                                  value={option.user_id}
                                  sx={{ justifyContent: "space-between" }}
                                >
                                  {`${option.first_name} ${option.last_name}`}
                                  {selected ? <CheckIcon color="info" /> : null}
                                </MenuItem>
                              )}
                            />
                            {errors.projectCoordinators &&
                              touched.projectCoordinators && (
                                <div className="error">
                                  {errors.projectCoordinators}
                                </div>
                              )}
                          </div>

                          <div className="add-project-box start-date-box col-md-4 mb-3">
                            <label htmlFor="startDate" className="form-label">
                              Start Date<span>*</span>
                            </label>
                            <input
                              type="date"
                              className="form-control"
                              id="startDate"
                              name="startDate"
                              value={values.startDate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {errors.startDate && touched.startDate && (
                              <div className="error">{errors.startDate}</div>
                            )}
                          </div>

                          <div className="add-project-box start-date-box col-md-4 mb-3">
                            <label htmlFor="endDate" className="form-label">
                              End Date
                            </label>
                            <input
                              type="date"
                              className="form-control"
                              id="endDate"
                              name="endDate"
                              value={values.endDate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </div>

                          <div className="add-project-box col-md-4 mb-3">
                            <label htmlFor="projectStatus" className="form-label">
                              Project Status<span>*</span>
                            </label>
                            <select
                              className="form-select"
                              id="projectStatus"
                              name="projectStatus"
                              value={values.projectStatus}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            >
                              <option selected value="New">
                                New
                              </option>
                              <option value="Active">Active</option>
                              <option value="Complete">Complete</option>
                              <option value="Inactive">Inactive</option>
                              <option value="On Hold">On Hold</option>
                              <option value="Archive">Archive</option>
                            </select>
                            {errors.projectStatus && touched.projectStatus && (
                              <div className="error">{errors.projectStatus}</div>
                            )}
                          </div>
                        </div>

                      </div>


                      {/* project address block */}
                      <div className="add-project-inner">
                        <h6 className="heading-content m-0 heading-text">Address</h6>
                        <div className="add-project-box">
                          <label
                            htmlFor="projectStreetAddress"
                            className="form-label"
                          >
                            Project Street Address<span>*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="projectStreetAddress"
                            id="projectStreetAddress"
                            placeholder="Enter project address"
                            value={values.projectStreetAddress}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          {errors.projectStreetAddress &&
                            touched.projectStreetAddress && (
                              <div className="error">
                                {errors.projectStreetAddress}
                              </div>
                            )}
                        </div>

                        <div className="address-section d-flex flex-wrap justify-content-between">
                          <div className="add-project-box">
                            <label htmlFor="projectSuburb" className="form-label">
                              Project Suburb<span>*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              name="projectSuburb"
                              id="projectSuburb"
                              placeholder="Enter project address"
                              value={values.projectSuburb}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {errors.projectSuburb && touched.projectSuburb && (
                              <div className="error">{errors.projectSuburb}</div>
                            )}
                          </div>


                          <div className="add-project-box">
                            <label htmlFor="projectState" className="form-label">
                              Project State<span>*</span>
                            </label>
                            <select
                              className="form-select"
                              id="projectState"
                              name="projectState"
                              value={values.projectState}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            >
                              <option value="" disabled>Select Type</option>
                              {Array.isArray(states) && states.length > 0 ? (
                                states.map((state_id) => (
                                  <option key={state_id.id} value={state_id.id}>
                                    {state_id.name}
                                  </option>
                                ))
                              ) : (
                                <option disabled>No project types available</option>
                              )}
                            </select>
                            {errors.projectState && touched.projectState && (
                              <div className="error">{errors.projectState}</div>
                            )}
                          </div>

                          <div className="add-project-box post-code">
                            <label htmlFor="postCode" className="form-label">
                              Post Code<span>*</span>
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              id="postCode"
                              placeholder="Enter Post Code"
                              name="postCode"
                              value={values.postCode}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {errors.postCode && touched.postCode && (
                              <div className="error">{errors.postCode}</div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* relevant document */}
                      <div className="add-project-inner">
                        <h6 className="heading-content m-0 heading-text">Relevant Documents</h6>
                        <div className="relevant-section row">
                          <div className="add-project-box project-plan-box col-xl-6">
                            <label htmlFor="projectPlan" className="form-label">
                              Project Plan
                            </label>

                            <div class="file-upload upload-file-block">
                              <img
                                src={uploadicon}
                                alt="upload-icon"
                                className="me-2 ms-2"
                              ></img>
                              <img src={pdfimg} class="file-icon" />
                              <span className="flex-grow-1 text-truncate">
                                <span class="file-name text-truncate">
                                  {values.projectPlan
                                    ? values.projectPlan.name
                                    : "No file selected"}
                                </span>

                              </span>
                              {values.projectPlan && (
                                <span
                                  className="remove-btn ms-2"
                                  onClick={() =>
                                    setFieldValue("projectPlan", null)
                                  }
                                >
                                  &#x2715;
                                </span>
                              )}

                              <label class="upload-btn">
                                Upload File
                                <input
                                  type="file"
                                  name="projectPlan"
                                  onChange={(e) =>
                                    setFieldValue("projectPlan", e.target.files[0])
                                  }
                                  accept=".pdf"
                                />
                              </label>
                            </div>
                            {errors.projectPlan && touched.projectPlan && (
                              <div className="error">{errors.projectPlan}</div>
                            )}
                          </div>

                          {/* other files */}
                          <div className="relevant-section col-xl-6">
                            <div className="add-project-box project-plan-box">
                              <label htmlFor="otherFiles" className="form-label">
                                Other Files
                              </label>

                              <div className="file-upload upload-file-block" style={{ alignItems: 'stretch' }}>
                                <div className="d-flex align-items-center flex-grow-1">
                                  <img
                                    src={uploadicon}
                                    alt="upload-icon"
                                    className="me-2 ms-2"
                                  />
                                  <img src={pdfimg} className="file-icon" />
                                  <span className="flex-grow-1 file-name text-truncate">
                                    {Array.isArray(values.otherFiles) &&
                                      values.otherFiles.length > 0
                                      ? values.otherFiles.map((file, index) => (
                                        <div key={index} className="file-name text-truncate">
                                          {file.name}{" "}

                                          <span
                                            className="remove-btn ms-2"
                                            onClick={() => {
                                              const updatedFiles =
                                                values.otherFiles.filter(
                                                  (_, i) => i !== index
                                                );
                                              setFieldValue(
                                                "otherFiles",
                                                updatedFiles
                                              );
                                            }}
                                          >
                                            &#x2715;
                                          </span>
                                        </div>
                                      ))
                                      : "No file selected"}
                                  </span>

                                </div>

                                <label className="upload-btn upload-blk">
                                  Upload File
                                  <input
                                    type="file"
                                    multiple
                                    name="otherFiles"
                                    onChange={(e) => {
                                      const files = Array.from(e.target.files);
                                      setFieldValue(
                                        "otherFiles",
                                        Array.isArray(values.otherFiles)
                                          ? [...values.otherFiles, ...files]
                                          : files
                                      );
                                    }}
                                  // accept=".pdf"
                                  />
                                </label>
                              </div>
                              {errors.otherFiles && touched.otherFiles && (
                                <div className="error">{errors.otherFiles}</div>
                              )}
                            </div>

                            {/* document url */}
                            <div className="add-project-box add-docs-url d-none">
                              <label htmlFor="document_urls" className="form-label">
                                All Documents URLs
                              </label>
                              <div>
                                {values.document_urls.map((url, index) => (
                                  <div key={index} className="d-flex align-items-center mb-2">
                                    <a href={url} target="_blank" rel="noopener noreferrer" className="document-url">
                                      {url}
                                    </a>
                                    <span
                                      className="remove-btn ms-2"
                                      onClick={() => {
                                        // Remove the URL from the array
                                        const updatedUrls = values.document_urls.filter(
                                          (_, i) => i !== index
                                        );
                                        setFieldValue("document_urls", updatedUrls);
                                      }}
                                    >
                                      &#x2715; {/* Close icon to remove */}
                                    </span>
                                  </div>
                                ))}
                              </div>

                              <input
                                type="text"
                                className="form-control"
                                id="document_urls"
                                name="newDocumentUrl"
                                value={values.newDocumentUrl || ""}
                                placeholder="Add Document URLs"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && values.newDocumentUrl) {
                                    e.preventDefault();

                                    setFieldValue("document_urls", [
                                      ...values.document_urls,
                                      values.newDocumentUrl,
                                    ]);

                                    setFieldValue("newDocumentUrl", "");
                                  }
                                }}
                              />

                              {errors.document_urls && touched.document_urls && (
                                <div className="error">{errors.document_urls}</div>
                              )}
                            </div>
                          </div>
                        </div>


                        <div className="add-project-box add-docs-url">
                          <label htmlFor="document_urls" className="form-label">
                            All Documents URLs
                          </label>
                          <div class="urlsClass">
                            {values.document_urls.map((url, index) => (
                              <div key={index} className="d-flex align-items-center mb-2">
                                <span className="me-2">
                                  <img src={urllink} alt="project-plan" className="link-class"></img>
                                </span>
                                <a href={url} target="_blank" rel="noopener noreferrer" className="document-url">
                                  {url}
                                </a>
                                <span
                                  className="remove-btn ms-2"
                                  onClick={() => {
                                    // Remove the URL from the array
                                    const updatedUrls = values.document_urls.filter(
                                      (_, i) => i !== index
                                    );
                                    setFieldValue("document_urls", updatedUrls);
                                  }}
                                >
                                  &#x2715; {/* Close icon to remove */}
                                </span>
                              </div>
                            ))}
                          </div>

                          <input
                            type="text"
                            className="form-control"
                            id="document_urls"
                            name="newDocumentUrl"
                            value={values.newDocumentUrl || ""}
                            placeholder="Add Document URLs"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && values.newDocumentUrl) {
                                e.preventDefault();

                                setFieldValue("document_urls", [
                                  ...values.document_urls,
                                  values.newDocumentUrl,
                                ]);

                                setFieldValue("newDocumentUrl", "");
                              }
                            }}
                          />

                          {errors.document_urls && touched.document_urls && (
                            <div className="error">{errors.document_urls}</div>
                          )}
                        </div>


                      </div>

                      {/* client details */}
                      <div className="add-project-inner">
                        <h6 className="heading-content m-0 heading-text">Client Details</h6>
                        <div className="row justify-content-between">
                          <div className="col-md-6 mb-4">
                            <label htmlFor="client_name" className="form-label">
                              Client Name
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="client_name"
                              placeholder="Enter Client Name"
                              name="client_name"
                              value={values.client_name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </div>
                          <div className="col-md-6">
                            <label htmlFor="client_email" className="form-label">
                              Client Email
                            </label>
                            <input
                              type="email"
                              className="form-control"
                              id="client_email"
                              placeholder="Enter Client Email"
                              name="client_email"
                              value={values.client_email}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {errors.client_email && touched.client_email && (
                              <div className="error">{errors.client_email}</div>
                            )}
                          </div>
                        </div>

                      </div>

                    </div>
                  </div>
                  <div className="text-center update-div d-flex align-items-center justify-content-center gap-2">
                    <button
                      type="button"
                      className="green-btn submit-btn"
                      onClick={() => navigate("/project-listing")}
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      class="green-btn submit-btn"
                      // style={{ marginLeft: "10px" }}
                      onClick={handleSubmit}
                    >
                      Save
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </>
  );

};

export default AddProject;
